/**
 * TrialExpiredOverlay — full-screen blocker shown when an expired-trial user
 * tries to use a gated feature. Conversion-focused with one-click upgrade.
 *
 * Used in two ways:
 *
 *   1) Globally — mount it once in App.js. It only renders when the user's
 *      trial has expired AND they hit one of the gated routes
 *      (see GATED_ROUTES constant). This keeps the rest of the site browsable.
 *
 *   2) Inline (forceShow=true) — a paid-only feature can force the overlay
 *      after the backend rejects with 402. Useful for click-level gating
 *      where the route itself is public but the action is paid.
 *
 * Props:
 *   - forceShow:  bool — bypass route check + show immediately
 *   - title:      override default headline
 *   - body:       override default body copy
 *   - reasonCode: 'trial_expired' | 'paid_only' | 'paid_only_trial' (changes copy)
 *   - onClose:    optional, when present renders an "Explore for now" exit
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Sparkles, ArrowRight, X } from 'lucide-react';
import useTrialStatus from '../hooks/useTrialStatus';

// Routes that should always force the overlay when an expired user lands on them.
// Public marketing pages (home, about, /membership, /login) are excluded so the
// user can still navigate to the upgrade page.
const GATED_ROUTES = [
  /^\/dashboard\/refer/,
  /^\/dashboard\/my-businesses/,
  /^\/dashboard\/investor-offers/,
  /^\/dashboard\/my-benefits/,
];

const reasonCopy = (code) => {
  if (code === 'paid_only' || code === 'paid_only_trial' || code === 'paid_only_expired') {
    return {
      title: 'Paid feature',
      body: 'This action is reserved for paid members. Upgrade to unlock posting, redemption, and referral payouts.',
    };
  }
  return {
    title: 'Your 3-day trial has ended',
    body: 'Thanks for trying IB Club. Upgrade to a paid membership to keep posting, redeeming, and earning rewards.',
  };
};

const TrialExpiredOverlay = ({ forceShow = false, title, body, reasonCode = 'trial_expired', onClose }) => {
  const { status } = useTrialStatus();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed flag whenever the route changes — overlay reappears on
  // re-entry to a gated route, even if user clicked "Continue browsing".
  useEffect(() => {
    setDismissed(false);
  }, [location.pathname]);

  const shouldRender = (() => {
    if (forceShow) return !dismissed;
    if (!status?.is_expired) return false;
    if (dismissed) return false;
    return GATED_ROUTES.some((re) => re.test(location.pathname));
  })();

  if (!shouldRender) return null;

  const copy = (() => {
    const base = reasonCopy(reasonCode);
    return {
      title: title || base.title,
      body: body || base.body,
    };
  })();

  return (
    <AnimatePresence>
      <motion.div
        key="trial-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        data-testid="trial-expired-overlay"
        className="fixed inset-0 z-[100] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-6"
      >
        <motion.div
          initial={{ y: 24, scale: 0.96, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          className="relative max-w-2xl w-full bg-slate-900 border border-[#d4af37]/40 shadow-2xl"
        >
          <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />

          {(onClose) && (
            <button
              onClick={() => { setDismissed(true); onClose?.(); }}
              data-testid="trial-overlay-close"
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-slate-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          )}

          <div className="p-8 md:p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/50 flex items-center justify-center mx-auto mb-5">
              <Lock className="text-[#d4af37]" size={28} />
            </div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3 font-semibold">
              {reasonCode === 'trial_expired' ? 'Trial Ended' : 'Members-only feature'}
            </p>
            <h2 className="text-3xl md:text-4xl font-playfair font-semibold text-white mb-3 leading-tight">
              {copy.title}
            </h2>
            <p className="text-slate-400 text-sm md:text-base leading-relaxed max-w-md mx-auto mb-7">
              {copy.body}
            </p>

            <div
              className="border border-dashed border-[#d4af37]/40 bg-[#d4af37]/[0.04] p-5 mb-7 text-center"
              data-testid="overlay-benefits-tease"
            >
              <Sparkles size={18} className="text-[#d4af37] mx-auto mb-2" />
              <p className="text-sm text-[#d4af37] font-medium tracking-wide">
                Benefits will be revealed soon
              </p>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                See pricing on the membership page and join the launch list.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-3 justify-center">
              <Link
                to="/membership"
                data-testid="trial-overlay-upgrade-cta"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 text-xs uppercase tracking-[0.2em] font-semibold bg-[#d4af37] hover:bg-[#e5c558] text-slate-950 transition-colors"
              >
                <Sparkles size={14} /> See Membership Tiers <ArrowRight size={14} />
              </Link>
              {onClose && (
                <button
                  type="button"
                  onClick={() => { setDismissed(true); onClose?.(); }}
                  data-testid="trial-overlay-continue"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium border border-white/10 text-slate-300 hover:border-white/25 transition-colors"
                >
                  Maybe later
                </button>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-5 tracking-wider">
              Cancel anytime · 7-day refund window
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TrialExpiredOverlay;
